import { type ReactNode, useRef, useState } from 'react';

import { Box, DialogProps } from '@mui/material';
import { t } from 'i18next';

import { FormContainerContext } from '~contexts';
import { FormWrapperRefProps } from '~interfaces/refProps';
import { Dimens } from '~themes';

import CustomDialog from './customDialog';

interface FormDialogProps extends Omit<DialogProps, 'children'> {
	open: boolean;
	submitLabel?: string;
	loading?: boolean;
	children: ReactNode;
	subtitle?: string;
	buttonsHint?: ReactNode;
	allowContentOverflow?: boolean;
}

// This component has been through multiple iterations. The core issue, the child component
// component has the form data, but this dialog has the submit button. And we need close
// interaction between these components
// Moving data back and forth through context, atoms or props causes unnecessary renders and
// updates. The current setup, where the form holds a reference to the child form seems to
// most efficient. This way the child on needs to expose and onSubmit function to trigger the
// onSubmit function in the child. This way we can also provide a return type within the child.
/**
 * A dialog to contain a form. Needs a single component which holds a ref
 */
const FormDialog = ({
	submitLabel = t('submit', { ns: 'actions' }),
	loading,
	title,
	subtitle,
	children,
	open,
	buttonsHint,
	allowContentOverflow,
	...dialogProps
}: FormDialogProps) => {
	const [disabled, setDisabled] = useState(true);
	const childRef = useRef<FormWrapperRefProps>(null);

	const handleSubmit = () => {
		if (!childRef.current?.onSubmit) {
			console.warn('FormDialog expect a onSubmit handler, did you forget it?');
			return;
		}

		childRef.current.onSubmit();
	};

	const handleCancel = () => {
		if (!childRef.current?.onCancel) {
			console.warn('FormDialog expect a onCancel handler, did you forget it?');
			return;
		}

		childRef.current.onCancel();
	};

	return (
		<CustomDialog
			{...dialogProps}
			title={title}
			subtitle={subtitle}
			titleAlignment='left'
			confirmButtonText={submitLabel}
			open={open}
			onConfirm={handleSubmit}
			onClose={handleCancel}
			onDecline={handleCancel}
			disabled={disabled}
			loading={loading}
			buttonsHint={buttonsHint}
			allowContentOverflow={allowContentOverflow}
			sx={{ p: Dimens.spacingMd }}
		>
			<Box sx={{ flexDirection: 'column' }}>
				<FormContainerContext
					value={{
						disabled: disabled,
						setDisabled: setDisabled,
						formRef: childRef,
					}}
				>
					{children}
				</FormContainerContext>
			</Box>
		</CustomDialog>
	);
};

export default FormDialog;
